import { useMemo } from "react";
import { StatCardData } from "../types";
import { formatCurrencyDecimal } from "../../../utils/helpers";

interface StatCardProps extends StatCardData {
  className?: string;
}

const colorVariants = {
  green: {
    bg: "bg-gradient-to-br from-emerald-500 to-green-600",
    icon: "bg-white/20",
    light: "bg-emerald-50 border-emerald-100",
    text: "text-emerald-600",
  },
  blue: {
    bg: "bg-gradient-to-br from-blue-500 to-indigo-600",
    icon: "bg-white/20",
    light: "bg-blue-50 border-blue-100",
    text: "text-blue-600",
  },
  orange: {
    bg: "bg-gradient-to-br from-orange-500 to-amber-600",
    icon: "bg-white/20",
    light: "bg-orange-50 border-orange-100",
    text: "text-orange-600",
  },
  red: {
    bg: "bg-gradient-to-br from-red-500 to-rose-600",
    icon: "bg-white/20",
    light: "bg-red-50 border-red-100",
    text: "text-red-600",
  },
  purple: {
    bg: "bg-gradient-to-br from-purple-500 to-violet-600",
    icon: "bg-white/20",
    light: "bg-purple-50 border-purple-100",
    text: "text-purple-600",
  },
  cyan: {
    bg: "bg-gradient-to-br from-cyan-500 to-teal-600",
    icon: "bg-white/20",
    light: "bg-cyan-50 border-cyan-100",
    text: "text-cyan-600",
  },
  yellow: {
    bg: "bg-gradient-to-br from-yellow-500 to-amber-500",
    icon: "bg-white/20",
    light: "bg-yellow-50 border-yellow-100",
    text: "text-yellow-600",
  },
};

export const StatCard = ({
  title,
  value,
  change,
  icon,
  color = "green",
  loading = false,
  prefix = "",
  suffix = "",
  className = "",
}: StatCardProps) => {
  const colors = colorVariants[color];

  const formattedValue = useMemo(() => {
    if (typeof value === "number") {
      return formatCurrencyDecimal(value);
    }
    return value;
  }, [value]);

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse ${className}`}>
        <div className="flex items-center justify-between">
          <div className="space-y-3 flex-1">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-8 bg-gray-200 rounded w-32"></div>
            <div className="h-3 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow ${className}`}>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl lg:text-3xl font-bold text-gray-900">
            {prefix}{formattedValue}{suffix}
          </p>
          {change && (
            <div className="flex items-center gap-1.5 pt-1">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                  change.type === "increase"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {change.type === "increase" ? (
                  <svg className="w-3 h-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l5-5 5 5M7 7l5 5 5-5" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-5 5-5-5m0 10l5-5 5 5" />
                  </svg>
                )}
                {change.value > 0 ? "+" : ""}{change.value}%
              </span>
              <span className="text-xs text-gray-500">{change.label}</span>
            </div>
          )}
        </div>
        {icon && (
          <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center shadow-lg`}>
            <div className="text-white">{icon}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Mini variant for smaller displays
export const StatCardMini = ({
  title,
  value,
  change,
  icon,
  color = "green",
  loading = false,
  prefix = "",
  suffix = "",
  className = "",
}: StatCardProps) => {
  const colors = colorVariants[color];

  const formattedValue = useMemo(() => {
    if (typeof value === "number") {
      return formatCurrencyDecimal(value);
    }
    return value;
  }, [value]);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse ${className}`}>
        <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-20"></div>
      </div>
    );
  }

  return (
    <div className={`${colors.light} rounded-xl p-4 border ${className}`}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
            <div className="text-white text-sm">{icon}</div>
          </div>
        )}
        <div>
          <p className="text-xs font-medium text-gray-500">{title}</p>
          <p className={`text-lg font-bold ${colors.text}`}>
            {prefix}{formattedValue}{suffix}
          </p>
          {change && (
            <span className={`text-xs ${change.type === "increase" ? "text-emerald-600" : "text-red-600"}`}>
              {change.type === "increase" ? "↑" : "↓"} {Math.abs(change.value)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatCard;
